import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import { useNavigate } from "react-router-dom";
import handleNotify from "../utils/helpers/handleNotify.js";

import FormConfirmPhoneNumber from "../features/CommonParts/FormConfirmPhoneNumber.jsx";
import Error from "../features/Error.jsx";

import ShimmerMultiLine from "../components/Shimmer/ShimmerMultiLine.jsx";
import Input from "../components/Input/Input.jsx";

import useConfirmPhoneNumber from "../hooks/useConfirmPhoneNumber.js";
import { APP_ROUTES } from "../router";

import useRedirect from "../hooks/useRedirect.js";
import useApiKey from "../hooks/useApiKey.js";

import apiCheckEdo from "../api/methods/apiCheckEdo.js";
import { apiConfirmPhoneNumber } from "../api/methods/verification.js";

import { apiCreateProcess, apiFetchStatusProcess } from "../api/methods/process.js";
import { ROISTAT_VISIT_ID } from "../utils/consts";

const ConfirmPhoneNumber = () => {
  const history = useNavigate();
  const { changeApiKey } = useApiKey();

  const redirect = useRedirect();
  const { mobileConnectionInfo } = useSelector(state => state);
  const { phoneNumber } = mobileConnectionInfo;

  const {
    smsCode,
    setSmsCode,
    remainingSeconds,
    isSuccessConfirmSmsCode,
    setIsSuccessConfirmSmsCode,
    isDisabledButton,
    setIsDisabledButton,
    isFetchData,
    hasError,
    setHasError,
    timerConfirmPhoneNumber,
    inputSmsCode,
    getSmsCode,
    checkRemainingSeconds,
    changeSmsCode,
  } = useConfirmPhoneNumber();

  useEffect(() => {
    if (!phoneNumber) {
      history(APP_ROUTES.MOBILE_CONNECTION.ROOT_ROUTE, { replace: true });
      return;
    }
    changeApiKey(null);

    (async () => {
      await getSmsCode(phoneNumber);
    })();

    return () => {
      clearInterval(timerConfirmPhoneNumber.current);
    };
  }, []);

  useEffect(() => {
    checkRemainingSeconds();
  }, [remainingSeconds]);

  /**
   * Подтвердить введенный код из СМС
   */
  const confirmSmsCode = async () => {
    try {
      await apiConfirmPhoneNumber(phoneNumber, +smsCode);
      await smsCodeIsTrue();
    } catch (error) {
      smsCodeIsFalse();
      handleNotify("error", error);
    }
  };

  const smsCodeIsTrue = async () => {
    setIsSuccessConfirmSmsCode(true);
    inputSmsCode.current.blur();

    clearInterval(timerConfirmPhoneNumber.current);
    await checkEdo();
  };

  const smsCodeIsFalse = () => {
    setSmsCode("");
    setIsDisabledButton(true);

    inputSmsCode.current.classList.add("input--invalid");
  };

  /**
   * Проверить есть ли у контрагента ЭДО
   */
  const checkEdo = async () => {
    try {
      const { key, hasEdo } = await apiCheckEdo(phoneNumber);
      changeApiKey(key);

      if (hasEdo) {
        await createProcess(key);

        const processStatus = await apiFetchStatusProcess(key);
        redirect(processStatus);
      } else {
        history(APP_ROUTES.MOBILE_CONNECTION.CONFIRM_PERSONAL_DATA_ROUTE);
      }
    } catch {
      setHasError(true);
    }
  };

  /**
   * ПРИМЕЧАНИЕ! У нового процесса мобильного подключения статус `Открыт`.
   * Когда пользователь дойдет до последнего шага он станет `Закрыт без выезда`
   * или `Закрыт с выездом`.
   *
   * В случае если пользователь попытается сразу же подключить услугу еще раз,
   * то процесс МПУ создан не будет, и будет отображен текст о лимите на создание,
   * так как запрещено создавать больше одного договора в течение 24 часов.
   * При этом не важно, какой будет указан адрес, на любом адресе будет та же ошибка.
   *
   * В случае если пользователь попытается подключить услугу спустя 24 часа,
   * то будет создан новый процесс МПУ
   *
   * @param {String} apiKey
   */
  const createProcess = async apiKey => {
    try {
      const {
        street: { id: streetId },
        house: { identifire: houseId },
        flat,
        fullName,
        phoneNumber,
        refUserId,
      } = mobileConnectionInfo;
      const company = localStorage.getItem("partnerProgram");

      await apiCreateProcess(
        apiKey,
        streetId,
        houseId,
        flat,
        fullName,
        phoneNumber,
        refUserId,
        ROISTAT_VISIT_ID,
        company || "-"
      );
    } catch (error) {
      throw error;
    }
  };

  if (isFetchData) return <ShimmerMultiLine count={2} />;
  if (hasError) return <Error />;

  return (
    <FormConfirmPhoneNumber
      isDisabledButton={isDisabledButton}
      isSuccessConfirmSmsCode={isSuccessConfirmSmsCode}
      phoneNumber={phoneNumber}
      remainingSeconds={remainingSeconds}
      getSmsCode={() => getSmsCode(phoneNumber)}
      confirmSmsCode={confirmSmsCode}
    >
      <Input
        placeholder="Введите код из СМС"
        onChange={changeSmsCode}
        value={smsCode}
        ref={inputSmsCode}
        maxLength={4}
        disabled={!remainingSeconds}
        autoComplete="off"
      />
    </FormConfirmPhoneNumber>
  );
};

export default ConfirmPhoneNumber;
